import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../../Components/filters/operators";

function ClientList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const currentParams = new URLSearchParams(searchParams);
    let needsUpdate = false;

    if (!currentParams.has("page")) {
      currentParams.set("page", "1");
      needsUpdate = true;
    }
    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10");
      needsUpdate = true;
    }
    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "client_name");
      needsUpdate = true;
    }
    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "asc");
      needsUpdate = true;
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Clients_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchClientsWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const filters = [];
      const processedFields = new Set();

      searchParams.forEach((value, key) => {
        if (
          key.endsWith("_operator") ||
          key.endsWith("_logic") ||
          key.endsWith("_label")
        )
          return;

        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "");
          if (processedFields.has(field)) return;
          processedFields.add(field);

          const operatorKey = `filter_${field}_operator`;
          const operator = searchParams.get(operatorKey) || "contains";

          const requiresValue = operatorRequiresValue(operator);
          if (!requiresValue || value) {
            let filterValue = requiresValue ? value : null;

            if (requiresValue && filterValue) {
              if (operator === "equals" || operator === "not_equals") {
                filterValue = filterValue
                  .split(",")
                  .map((v) => v.trim().toLowerCase());
              } else if (operator === "between" || operator === "not_between") {
                filterValue = filterValue.split(",").map((v) => v.trim());
              }
            }

            filters.push({ field, operator, value: filterValue });
          }
        }
      });

      const search = searchParams.get("search");

      const requestBody = {
        organization_id: orgId,
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        pageSize: Math.min(
          Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          100
        ),
        sort: {
          field: searchParams.get("sortBy") || "client_name",
          direction: searchParams.get("sortOrder") || "asc",
        },
        filters,
        ...(search && search.trim() !== "" && { search: search.trim() }),
      };

      const response = await fetch(
        `${MAIN_URL}/api/project-management/clients-filtered`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const normalized = result.data.map((item) => {
          const countryLabel =
            (typeof item.country === "string" && item.country !== "-"
              ? item.country
              : null) ??
            item.country_name ??
            (typeof item.country_id === "string" ? item.country_id : null) ??
            "-";
          const stateLabel =
            (typeof item.state === "string" && item.state !== "-"
              ? item.state
              : null) ??
            item.state_name ??
            (typeof item.state_id === "string" ? item.state_id : null) ??
            "-";
          const cityLabel =
            (typeof item.city === "string" && item.city !== "-"
              ? item.city
              : null) ??
            item.city_name ??
            (typeof item.city_id === "string" ? item.city_id : null) ??
            "-";

          return {
            ...item,
            id: item.organization_client_id ?? item.id,
            country: countryLabel,
            state: stateLabel,
            city: cityLabel,
            country_id: countryLabel,
            state_id: stateLabel,
            city_id: cityLabel,
          };
        });
        setClients(normalized);

        if (result.pagination) {
          const pageSize =
            result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage =
            Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page: result.pagination.page || requestedPage,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData({
            page: 1,
            pageSize: 10,
            total: normalized.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          });
        }
      } else {
        setClients([]);
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        });
        toast.error(result.message || result.error || "Failed to fetch clients");
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching clients:", err);
      toast.error("Failed to fetch clients");
      setClients([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, searchParams]);

  useEffect(() => {
    fetchClientsWithFilters();
  }, [fetchClientsWithFilters]);

  return (
    <>
      <Layout4
      heading="Clients"
        Route="/client"
      btnName="Add Client"
        showAddButton={true}
      add_action="ADD_ORGANIZATION_CLIENT"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          gridKey="Clients_grid"
          tableName="organization_clients"
          filterModule="organization_clients"
          primaryKey="id"
          heading="organization_clients"
          data={clients}
          sortname={"client_name"}
          showActions={true}
          Route="/client"
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={[
            "EDIT_ORGANIZATION_CLIENT",
            "DELETE_ORGANIZATION_CLIENT",
          ]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
          viewFunction={(obj) => navigate(`/client/view/${obj?.id}`)}
          infoFunction={(obj) => navigate(`/client/info/${obj?.id}`)}
      info_action="VIEW_CLIENT_PROJECT_REPORT"
    />
      )}
    </>
  );
}

export default ClientList;
